import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FiMaximize2, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useLightbox } from '../../context/LightboxContext';
import blogService from '../../services/blogService';
import { getImageUrl } from '../../utils/imageUrl';
import './BlogSection.css';

const BlogSection = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [scrollProgress, setScrollProgress] = useState(0);
    const gridRef = useRef(null);
    const { openLightbox } = useLightbox();

    const handleScroll = () => {
        const el = gridRef.current;
        if (!el) return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        if (maxScroll <= 0) {
            setScrollProgress(0);
            return;
        }
        const progress = Math.min(100, Math.max(0, (el.scrollLeft / maxScroll) * 100));
        setScrollProgress(progress);
    };

    const scrollByAmount = (direction) => {
        const el = gridRef.current;
        if (!el) return;
        const offset = direction === 'left' ? -300 : 300;
        el.scrollBy({ left: offset, behavior: 'smooth' });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                // Show 4 curated blogs on homepage
                const { blogs: list } = await blogService.getAll(1, 4, '');
                if (!cancelled) setBlogs(Array.isArray(list) ? list.slice(0, 4) : []);
            } catch (e) {
                console.error('Failed to load blog posts:', e);
                if (!cancelled) setBlogs([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const displayedBlogs = blogs.slice(0, 4);

    return (
        <section className="blog-section" aria-label="Latest blog posts and appliance buying guides">
            <div className="container">
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                    <span className="section-eyebrow">BUYING GUIDES &amp; INSIGHTS</span>
                    <h2 className="section-title">Latest from Our Blogs</h2>
                    <p style={{ color: 'var(--gray-500)', fontSize: '0.95rem', maxWidth: '520px', margin: '0.35rem auto 0' }}>
                        Expert tips on energy-efficient cooling, food preservation, and installment budgeting
                    </p>
                </div>

                {loading ? (
                    <div className="blogs-grid">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="blog-card blog-section-skeleton" />
                        ))}
                    </div>
                ) : displayedBlogs.length === 0 ? (
                    <p className="blog-section-empty">No posts yet. Check back soon.</p>
                ) : (
                    <>
                        <div
                            ref={gridRef}
                            onScroll={handleScroll}
                            className="blogs-grid"
                        >
                            {displayedBlogs.map((blog) => {
                                const img = getImageUrl(blog.featured_image || blog.image);
                                const slugPath = `/blogs/${encodeURIComponent(blog.slug)}`;
                                return (
                                    <article key={blog._id} className="blog-card">
                                        <div className="blog-image-wrap">
                                            <Link to={slugPath} className="blog-image">
                                                {img ? (
                                                    <img
                                                        src={img}
                                                        alt={blog.title}
                                                        loading="lazy"
                                                        onError={(e) => {
                                                            e.currentTarget.src = 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=900&auto=format&fit=crop';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="blog-image-placeholder" />
                                                )}
                                            </Link>
                                            {img && (
                                                <button
                                                    type="button"
                                                    className="blog-zoom-btn"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        openLightbox(img, blog.title, blog.title);
                                                    }}
                                                    title="Tap to enlarge photo"
                                                    aria-label="Enlarge image"
                                                >
                                                    <FiMaximize2 />
                                                </button>
                                            )}
                                        </div>
                                        <div className="blog-content">
                                            <div className="blog-meta">
                                                <span className="blog-category">
                                                    {blog.category || 'Buying Guide'}
                                                </span>
                                            </div>
                                            <Link to={slugPath} className="blog-title-link">
                                                <h3 className="blog-title">{blog.title}</h3>
                                            </Link>
                                            <p className="blog-excerpt">{blog.excerpt}</p>
                                            <Link to={slugPath} className="blog-link">
                                                Read more →
                                            </Link>
                                        </div>
                                    </article>
                                );
                            })}
                        </div>

                        {/* Mobile-Only Interactive Carousel Slidebar Indicator */}
                        <div className="carousel-slidebar-wrap">
                            <button
                                type="button"
                                className="carousel-slidebar-nav-btn carousel-slidebar-nav-btn--left"
                                onClick={() => scrollByAmount('left')}
                                aria-label="Scroll carousel left"
                            >
                                <FiChevronLeft />
                            </button>
                            <div className="carousel-slidebar-track">
                                <div
                                    className="carousel-slidebar-thumb"
                                    style={{ left: `${scrollProgress * 0.7}%` }}
                                />
                            </div>
                            <button
                                type="button"
                                className="carousel-slidebar-nav-btn carousel-slidebar-nav-btn--right"
                                onClick={() => scrollByAmount('right')}
                                aria-label="Scroll carousel right"
                            >
                                <FiChevronRight />
                            </button>
                        </div>
                    </>
                )}

                <div className="blog-cta">
                    <Link to="/blogs" className="btn btn-primary">
                        View All Blogs &amp; Buying Guides
                    </Link>
                </div>
            </div>
        </section>
    );
};

export default BlogSection;
